/** A candidate's activity on one requirement, shown as a LOG: time · status · actor · what happened
 *  — the layout of an automation run log (Mailbox → PDF reader → PO matcher …), applied to hiring
 *  (Intake → Mailer → Candidate → Resume scanner → AI interviewer → Scoring).
 *
 *  Data: GET /candidacies/{id}/timeline (recruiters) or GET /admin/candidacies/{id}/timeline (the
 *  platform console) — both built by apps/api/src/utils/candidacyTimeline.ts. Read-only. */
import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, CircleCheck, CircleDot, CircleX, TriangleAlert } from "lucide-react";
import { api } from "../lib/api.js";

/** Mirrors apps/api/src/utils/candidacyTimeline.ts TimelineEvent. */
export interface ActivityEvent {
  at: string | null;
  kind: string;
  actor: string;
  title: string;
  detail?: string;
  tone: "done" | "info" | "warn" | "bad";
}

const two = (n: number) => String(n).padStart(2, "0");
/** "03 Oct 15:11:15" — a log spans days, so the date leads; seconds keep same-minute steps ordered. */
function stamp(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
  return `${day} ${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}`;
}

function ToneIcon({ tone }: { tone: ActivityEvent["tone"] }): ReactNode {
  const p = { size: 15, "aria-hidden": true } as const;
  if (tone === "done") return <CircleCheck {...p} />;
  if (tone === "warn") return <TriangleAlert {...p} />;
  if (tone === "bad") return <CircleX {...p} />;
  return <CircleDot {...p} />;
}

const TONE_WORD: Record<ActivityEvent["tone"], string> = { done: "Done", info: "Info", warn: "Warning", bad: "Failed" };

/** The log rows. `path` is the timeline endpoint (recruiter or admin). */
export function ActivityLog({ path }: { path: string }): ReactNode {
  const q = useQuery({
    queryKey: ["candidacy-timeline", path],
    queryFn: () => api.get<{ events: ActivityEvent[] }>(path),
    staleTime: 15_000,
  });
  if (q.isLoading) return <p className="act-note">Loading activity…</p>;
  if (q.isError) {
    return (
      <p className="act-note">
        Couldn't load this candidate's activity.{" "}
        <button type="button" className="act-retry" onClick={() => void q.refetch()}>
          Try again
        </button>
      </p>
    );
  }
  const events = q.data?.events ?? [];
  if (events.length === 0) return <p className="act-note">No activity recorded yet.</p>;

  return (
    <div className="act-log">
      <ol className="act-rows">
        {events.map((e, i) => (
          <li key={`${e.kind}-${i}`} className={`tone-${e.tone}${e.at ? "" : " is-now"}`}>
            {e.at ? (
              <time dateTime={e.at}>{stamp(e.at)}</time>
            ) : (
              <time title="Current state — when it was set isn't recorded">now</time>
            )}
            <span className="act-ico" title={TONE_WORD[e.tone]}>
              <ToneIcon tone={e.tone} />
            </span>
            <b className="act-who">{e.actor}</b>
            <span className="act-msg">
              {e.title}
              {e.detail && <small>{e.detail}</small>}
            </span>
          </li>
        ))}
      </ol>
      <p className="act-note">"Emailed" = accepted by our email provider; inbox opens aren't tracked. "now" rows are current state.</p>
    </div>
  );
}

/** Recruiter screens: the log for a candidate on the caller's own requirement. */
export function CandidateTimeline({ candidacyId }: { candidacyId: string }): ReactNode {
  return <ActivityLog path={`/candidacies/${candidacyId}/timeline`} />;
}

/** A collapsible log group — header line (who · context · state) then the rows, loaded on open. */
export function ActivityGroup({
  title,
  subtitle,
  status,
  path,
  defaultOpen = false,
}: {
  title: string;
  subtitle?: string;
  status?: string;
  path: string;
  defaultOpen?: boolean;
}): ReactNode {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`act-group${open ? " is-open" : ""}`}>
      <button type="button" className="act-group-h" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
        <span className="act-group-who">
          <b>{title}</b>
          {subtitle && <small>{subtitle}</small>}
        </span>
        {status && <span className="act-group-status">{status}</span>}
      </button>
      {open && (
        <div className="act-group-b">
          <ActivityLog path={path} />
        </div>
      )}
    </div>
  );
}
